import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';
import { sectionRenderer } from '@theme/section-renderer';

/**
 * Phone cart bar (sections/WYN-sticky-cart-bar.liquid).
 *
 * The count and total come from Liquid, so on every cart update the section re-renders
 * through the Section Rendering API instead of formatting money here. An update that
 * arrives while a render is in flight queues one more render, so quick successive adds
 * never leave a stale count. The summary link opens the cart drawer when it's available
 * and falls back to the cart page.
 *
 * @extends {Component}
 */
class StickyCartBar extends Component {
  /** @type {AbortController | undefined} */
  #listeners;

  #rendering = false;
  #queued = false;

  connectedCallback() {
    super.connectedCallback();

    this.#listeners?.abort();
    this.#listeners = new AbortController();
    const { signal } = this.#listeners;

    document.addEventListener(ThemeEvents.cartUpdate, this.#refresh, { signal });
    // Back/forward cache: the cart may have changed on another page.
    window.addEventListener('pageshow', (event) => event.persisted && this.#refresh(), { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#listeners?.abort();
  }

  /**
   * Opens the cart drawer instead of following the link to the cart page.
   * @param {MouseEvent} event
   */
  openCart(event) {
    const drawer = /** @type {(HTMLElement & { open?: () => void }) | null} */ (
      document.querySelector('cart-drawer-component')
    );
    if (typeof drawer?.open !== 'function') return;

    event.preventDefault();
    drawer.open();
  }

  #refresh = async () => {
    const { sectionId } = this.dataset;
    if (!sectionId) return;

    if (this.#rendering) {
      this.#queued = true;
      return;
    }

    this.#rendering = true;
    try {
      await sectionRenderer.renderSection(sectionId, { cache: false });
    } catch {
      // A failed refresh keeps the current bar; the next cart update renders it again.
    } finally {
      this.#rendering = false;
      if (this.#queued) {
        this.#queued = false;
        this.#refresh();
      }
    }
  };
}

if (!customElements.get('sticky-cart-bar')) {
  customElements.define('sticky-cart-bar', StickyCartBar);
}
